import { useState } from "react";
import { FileText, LayoutDashboard, Settings, LogOut, ShieldCheck, CreditCard, History, HelpCircle, Menu, Layers, Users, Headset, ScrollText, FlaskConical, Building2, Sparkles, AlertTriangle, Network, Clock, UserCog, DollarSign, Receipt } from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import { Link, useLocation } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { useStaffIndustryView, INDUSTRY_OPTIONS } from "@/hooks/useStaffIndustryView";
import { UpgradeModal } from "@/components/upgrade/UpgradeModal";
import type { FeatureKey } from "@/hooks/useFeatureAccess";
import { NotificationCenter } from "@/components/layout/NotificationCenter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { profile, isSuperAdmin, isSupportRole, isSalesRole, isDemoUser, signOut, subscription } = useAuth();
  const { whiteLabelSettings } = useWhiteLabel();
  const isMobile = useIsMobile();
  const { isStaffOrAdmin, effectiveIndustry } = useStaffIndustryView();
  const activeIndustryLabel = INDUSTRY_OPTIONS.find(o => o.value === effectiveIndustry)?.label || effectiveIndustry;
  const [upgradeFeature, setUpgradeFeature] = useState<FeatureKey | null>(null);

  const isAgencyOrAbove = isDemoUser || subscription.tier === "agency" || subscription.tier === "enterprise" || subscription.tier === "testing";

  // Role-based navigation:
  // - Support Staff: Dashboard, Support (Staff), Settings only
  // - Support Manager: Same as Staff + Staff tab for team management
  // - Super Admin: All above + Admin tab
  const isStaffOnly = isSupportRole && !isSuperAdmin;

  // Grouped navigation: Workspace (daily work) → Internal (staff tools) → Admin (super_admin)
  type NavItem = { href: string; label: string; icon: typeof LayoutDashboard; lockedFeature?: FeatureKey };
  type NavGroup = { label: string; items: NavItem[] };

  const workspaceItems: NavItem[] = [
    { href: "/", label: isStaffOnly ? "My Performance" : "Dashboard", icon: isStaffOnly ? Headset : LayoutDashboard },
    ...(!isStaffOnly ? [
      { href: "/builder", label: "New Proposal", icon: FileText },
      ...(effectiveIndustry === "security" ? [{ href: "/threats", label: "Threats", icon: AlertTriangle }] : []),
      // Always show Bulk + Team — clicking opens upgrade modal if not Agency+
      isAgencyOrAbove
        ? { href: "/bulk", label: "Bulk", icon: Layers }
        : { href: "/bulk", label: "Bulk", icon: Layers, lockedFeature: "bulk" as FeatureKey },
      isAgencyOrAbove
        ? { href: "/team", label: "Team", icon: Users }
        : { href: "/team", label: "Team", icon: Users, lockedFeature: "team" as FeatureKey },
      { href: "/contracts", label: "Contracts", icon: ScrollText },
      { href: "/invoices", label: "Invoices", icon: Receipt },
      { href: "/history", label: "History", icon: History },
    ] : []),
    ...((!isSupportRole || isSuperAdmin) ? [{ href: "/support", label: "Support", icon: HelpCircle }] : []),
    ...(!isStaffOnly ? [{ href: "/pricing", label: "Pricing", icon: CreditCard }] : []),
    { href: "/settings", label: "Settings", icon: Settings },
    // Industries nav is staff/admin-only — regular users are locked to their signed-up industry
    ...(isStaffOrAdmin ? [{ href: "/industries", label: "Industries", icon: Building2 }] : []),
    { href: "/time-clock", label: "Time Clock", icon: Clock },
  ];

  const internalItems: NavItem[] = [
    ...((isSuperAdmin || isSalesRole) ? [{ href: "/sales-inquiries", label: "Sales", icon: DollarSign }] : []),
    ...((isSuperAdmin || isSupportRole) ? [
      { href: "/staff", label: "Staff", icon: Headset },
      { href: "/org-chart", label: "Org Chart", icon: Network },
      { href: "/time-admin", label: "Time Admin", icon: Clock },
    ] : []),
  ];

  const adminItems: NavItem[] = isSuperAdmin ? [
    { href: "/employee-management", label: "Employees", icon: UserCog },
    { href: "/admin", label: "Admin", icon: ShieldCheck },
  ] : [];

  const navGroups: NavGroup[] = [
    { label: "Workspace", items: workspaceItems },
    { label: "Internal", items: internalItems },
    { label: "Admin", items: adminItems },
  ].filter(g => g.items.length > 0);

  const navItems = navGroups.flatMap(g => g.items);
  const activeItem = navItems.find((item) => item.href === location.pathname);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container flex items-center justify-between px-4 py-2 min-h-[4rem] gap-2">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-9 w-9 rounded-lg object-contain" />
            {whiteLabelSettings.removeBranding && whiteLabelSettings.companyName ? (
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold tracking-tight text-foreground">{whiteLabelSettings.companyName}</h1>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Security Operations Platform</p>
              </div>
            ) : !whiteLabelSettings.removeBranding ? (
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold tracking-tight text-foreground">Sentinel Forge</h1>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Security Operations Platform</p>
              </div>
            ) : null}
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Desktop nav */}
            {!isMobile && (
              <nav className="hidden lg:flex items-center gap-0.5 flex-wrap justify-end">
                {navGroups.map((group, groupIdx) => (
                  <div key={group.label} className="flex items-center gap-0.5">
                    {groupIdx > 0 && (
                      <span aria-hidden className="mx-1 h-5 w-px bg-border" />
                    )}
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.href;
                      const baseClasses = cn(
                        "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all whitespace-nowrap",
                        isActive
                          ? "bg-primary/10 text-primary shadow-glow"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      );
                      if (item.lockedFeature) {
                        return (
                          <button
                            key={item.href}
                            type="button"
                            onClick={() => setUpgradeFeature(item.lockedFeature!)}
                            className={cn(baseClasses, "relative")}
                          >
                            <item.icon className="h-3.5 w-3.5" />
                            {item.label}
                            <Sparkles className="h-3 w-3 text-primary/70" />
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={baseClasses}
                          data-tour={item.href === "/history" ? "nav-history" : item.href === "/settings" ? "nav-settings" : undefined}
                        >
                          <item.icon className="h-3.5 w-3.5" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
            )}

            {/* Mobile/Tablet dropdown nav */}
            <div className="lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 border-border">
                    <Menu className="h-4 w-4" />
                    <span className="hidden sm:inline">{activeItem?.label || "Menu"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto bg-popover border-border z-50">
                  {navGroups.map((group, groupIdx) => (
                    <div key={group.label}>
                      {groupIdx > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                        {group.label}
                      </DropdownMenuLabel>
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.href;
                        if (item.lockedFeature) {
                          return (
                            <DropdownMenuItem
                              key={item.href}
                              onSelect={(e) => {
                                e.preventDefault();
                                setUpgradeFeature(item.lockedFeature!);
                              }}
                              className="cursor-pointer"
                            >
                              <item.icon className="h-4 w-4 mr-2" />
                              <span className="flex-1">{item.label}</span>
                              <Sparkles className="h-3 w-3 text-primary/70" />
                            </DropdownMenuItem>
                          );
                        }
                        return (
                          <DropdownMenuItem key={item.href} asChild>
                            <Link
                              to={item.href}
                              data-tour={item.href === "/history" ? "nav-history" : item.href === "/settings" ? "nav-settings" : undefined}
                              className={cn(
                                "flex items-center gap-2 w-full cursor-pointer",
                                isActive && "text-primary font-medium bg-primary/10"
                              )}
                            >
                              <item.icon className="h-4 w-4" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 border-l border-border pl-2 sm:pl-4">
              <NotificationCenter />
              {isStaffOrAdmin && (
                <Badge variant="outline" className="gap-1 border-primary/50 text-primary text-[10px] px-2 py-0.5">
                  <Building2 className="h-3 w-3" />
                  {activeIndustryLabel}
                </Badge>
              )}
              {isDemoUser && (
                <Badge variant="outline" className="gap-1 border-warning text-warning text-[10px] px-2 py-0.5">
                  <FlaskConical className="h-3 w-3" />
                  Demo Mode
                </Badge>
              )}
              <span className="text-xs text-muted-foreground truncate max-w-[80px] sm:max-w-[120px] hidden sm:inline">
                {profile?.full_name || profile?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="h-8 text-muted-foreground hover:text-foreground gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden lg:inline text-sm">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-4 sm:py-8 px-4">{children}</main>

      {upgradeFeature && (
        <UpgradeModal
          open={!!upgradeFeature}
          onOpenChange={(open) => { if (!open) setUpgradeFeature(null); }}
          feature={upgradeFeature}
        />
      )}
    </div>
  );
};

export default AppLayout;
